import { Box, useTheme } from "@mui/material";
import { tokens } from "../../../theme";


//import Echeck from "./views/Echeck";
import { useEffect, useState, useRef } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { InboundChart } from "../../../components/charts/dashboard/fulfillment/InboundChart";
import { OrderHistoryChart } from "../../../components/charts/dashboard/fulfillment/OrderHistoryChart";
import { OrderlinesChart } from "../../../components/charts/dashboard/fulfillment/OrderlinesChart";
import { OrdersChart } from "../../../components/charts/dashboard/fulfillment/OrdersChart";
import { ReturnChart } from "../../../components/charts/dashboard/fulfillment/ReturnChart";
import { useTopbar } from "../../../contexts/TopbarProvider";
import { useGetDashboardOrderHistoryQuery } from "../../../newapi/reports/fulfillment/reportFulfillmentSlice";
import { DashboardDeltaPanel } from "./components/DashboardDeltaPanel";
import { DashboardSwitcher } from "./components/DashboardSwitcher";
import DashboardViewer from "../../../components/global/StockhubBI/DashboardViewer";
import { useGetBiDashboardsQuery } from "../../../newapi/global/businessIntelligence";
import { AnimatePresence, motion } from "framer-motion";


const Dashboard = () => {
    const { t, ready,i18n } = useTranslation();
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);

    // const sets = [
    //     {
    //         label:"delta",
    //         values:[-50,-20,10,200,60]
    //     },
    //     {
    //         label:"orders",
    //         values:[100,150,100,20,50]
    //     },
    //     {
    //         label:"pick",
    //         values:[300,0,10,30,50]
    //     },
    //     {
    //         label:"pack",
    //         values:[50,250,10,30,50]
    //     },
    // ];

    const {data: orderHistoryData, isLoading: isDashboardOrderHistoryLoading, isFetching: isDashboardOrderHistoryFetching, isError: isDashboardOrderHistoryError, error: dashboardOrderHistoryError} = useGetDashboardOrderHistoryQuery();

    // console.log(data);
    const {showExtraTitle,clearExtraTitle } = useTopbar();

    const { data: dashboardsData, isError: dashboardsError } = useGetBiDashboardsQuery();
    const dashboards = dashboardsData?.data ?? [];
    const primary = dashboards.find((d) => d.isPrimary) ?? null;

    // null = the built-in charts; otherwise a BI dashboard id. Defaults to the user's primary once it loads.
    // `initialized` gates the first paint until we know the default, so the built-in never flashes before the primary.
    const [selectedId, setSelectedId] = useState(null);
    const [initialized, setInitialized] = useState(false);
    const initRef = useRef(false);

    // BI dashboards are scoped to the active warehouse/customer/environment; switching context (which dispatches
    // resetApiState) gives a different list + primary, so a selection from the old context is stale and would
    // 404. Reset the one-time init so the default is re-derived for the new context.
    const contextKey = useSelector((s) => `${s.settings?.environment?.selectedEnvironmentType}:${s.settings?.environment?.selectedId}:${s.warehouse?.activeId}`);
    const contextRef = useRef(contextKey);
    useEffect(() => {
        if (contextRef.current === contextKey) return;
        contextRef.current = contextKey;
        initRef.current = false;
        setSelectedId(null);
        setInitialized(false);
    }, [contextKey]);

    useEffect(() => {
        // Initialise once the list settles — loaded OR failed. On failure we leave selectedId null so the
        // built-in default dashboard renders instead of the page hanging blank.
        if (initRef.current || (!dashboardsData && !dashboardsError)) return;
        if (primary) setSelectedId(primary.id);
        initRef.current = true;
        setInitialized(true);
    }, [dashboardsData, primary, dashboardsError]);

    // The switcher takes over the topbar title slot when there are saved dashboards (it doubles as the page
    // title, showing the active one). Wait until the list has loaded before setting any title, so the big
    // "Dashboard" text never flashes and then bonks to the switcher. With no dashboards, keep the plain title.
    useEffect(() => {
        if (!initialized) return;
        showExtraTitle(dashboards.length ? <DashboardSwitcher selectedId={selectedId} onSelect={setSelectedId} /> : t("dashboard"));

        return () => {
            clearExtraTitle();
        };
    }, [showExtraTitle,clearExtraTitle,selectedId,dashboards.length,t,initialized])


    return (
        <AnimatePresence mode="wait">
            {initialized && (selectedId ? (
                <motion.div
                    key={`dashboard-${selectedId}`}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.25 }}
                    style={{ width: "100%" }}
                >
                    <DashboardViewer id={String(selectedId)}
                        hideControls={true}
                    />
                </motion.div>
            ) : (
                <motion.div
                    key="dashboard-default"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.25 }}
                    style={{ width: "100%" }}
                >
                    <Box sx={{background:colors.bg['tertiary'], width:1,paddingTop:9,}}>


                        <Box sx={{ px:4, mt:2 }}>
                            <DashboardDeltaPanel/>
                        </Box>
                        {ready ?
                            <Box

                                sx={{
                                    mt:4,
                                    // width:1,
                                    minHeight:"calc(100% - 350.5px)",
                                    background: `${theme.palette.mode === "dark" ? colors.grey[200] : colors.grey[0]}`,
                                    position:"relative",
                                    zIndex:2,

                                    pt:4,
                                    pb:4,
                                    px:4,
                                    // display:"flex",
                                    // alignItems:"flex-start",

                                    "&:before" : {
                                        content: "''",
                                        display:"block",
                                        width:"32px",
                                        height:"32px",
                                        position:"absolute",
                                        left:0,
                                        zIndex:1,
                                        backgroundColor: `${theme.palette.mode === "dark" ? colors.grey[200] : colors.grey[0]}`,
                                        top:"0px",
                                        height:"48px",
                                        width:"24px",
                                        borderTopLeftRadius:"24px",
                                        boxShadow: `0 -24px 0 0 ${colors.bg["tertiary"]}`,
                                        transition:"250ms"
                                    },

                                    "&:after" : {
                                        content: "''",
                                        display:"block",
                                        width:"32px",
                                        height:"32px",
                                        position:"absolute",
                                        top:0,
                                        zIndex:1,
                                        backgroundColor: `${theme.palette.mode === "dark" ? colors.grey[200] : colors.grey[0]}`,
                                        right:0,
                                        height:"48px",
                                        width:"24px",
                                        borderTopLeftRadius:"24px",
                                        boxShadow: `0 -24px 0 0 ${colors.bg["tertiary"]}`,
                                        transform:"scaleX(-1)",
                                        transition:"250ms"
                                    },

                                    "@media screen and (max-width: 47.9375em)" : {
                                        flexDirection:"column",
                                        "&:after, &:before" : {
                                            top:"-0px"
                                        }
                                    }
                                }}
                            >
                                <Box
                                    sx={{
                                        display:'grid',
                                        gridTemplateColumns: '2fr 4fr',
                                        gap:3,
                                        "@media screen and (max-width: 1024px)" : {
                                            gridTemplateColumns: "2fr 2fr",
                                        },
                                        "@media screen and (max-width: 768px)" : {
                                            gridTemplateColumns: "repeat(2, 1fr)"
                                        }
                                    }}
                                >
                                    <OrdersChart/>
                                    <OrderHistoryChart/>
                                </Box>
                                <Box
                                    sx={{
                                        mt:3,
                                        display:'grid',
                                        gridTemplateColumns: '2fr 4fr',
                                        gap:3,
                                        "@media screen and (max-width: 1024px)" : {
                                            gridTemplateColumns: "repeat(2, 1fr)"
                                        }
                                    }}
                                >
                                    <OrderlinesChart/>
                                    <Box
                                        sx={{
                                            display:"grid",
                                            gridTemplateColumns: "repeat(2, 1fr)",
                                            // gridTemplateRows:1,
                                            gap:3,
                                            "@media screen and (max-width: 1024px)" : {
                                                gridArea: "2 / 1 / 3 / 3",
                                            },
                                            "@media screen and (max-width: 768px)" : {
                                                // flexWrap: "wrap"
                                                gridTemplateColumns:"1fr",
                                                gridTemplateRows:"repeat(2, 1fr)",
                                            }
                                        }}
                                    >
                                        <InboundChart/>
                                        <ReturnChart/>

                                    </Box>

                                </Box>
                                
                            </Box>
                        :null}
                    </Box>
                </motion.div>
            ))}
        </AnimatePresence>
    );
};

export default Dashboard;